import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { cacheUser, clearCachedUser, loadCachedUser } from './authCache';

export interface User {
  sub: string;
  email?: string;
  name?: string;
}

interface AuthState {
  user: User | null;
  loading: boolean;
}

interface AuthContextValue extends AuthState {
  login: () => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Offline-aware auth. On mount (and whenever connectivity returns) it calls
 * GET /auth/me and reconciles three outcomes:
 *   - 200            -> authenticated; cache the user for offline reopens.
 *   - 401            -> server reachable and says signed out; clear the cache.
 *   - network error  -> can't reach the server; fall back to the cached user so
 *                       a valid, installed PWA opens instead of bouncing to an
 *                       unreachable IdP. Only signs out if there's no (unexpired)
 *                       cached user.
 *
 * `login()` starts the OIDC redirect; `logout()` clears server + local state.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ user: null, loading: true });

  useEffect(() => {
    let cancelled = false;

    const fallbackToCache = () => {
      const cached = loadCachedUser();
      if (!cancelled) setState({ user: cached, loading: false });
    };

    const validate = async () => {
      try {
        const resp = await fetch('/auth/me', { credentials: 'include' });
        if (resp.ok) {
          const user = (await resp.json()) as User;
          cacheUser(user);
          if (!cancelled) setState({ user, loading: false });
        } else if (resp.status === 401) {
          clearCachedUser();
          if (!cancelled) setState({ user: null, loading: false });
        } else {
          // Unexpected server status (5xx, proxy hiccup): treat like offline.
          fallbackToCache();
        }
      } catch {
        fallbackToCache();
      }
    };

    void validate();
    // Re-check when the connection returns so an expired session is caught.
    const onOnline = () => void validate();
    window.addEventListener('online', onOnline);
    return () => {
      cancelled = true;
      window.removeEventListener('online', onOnline);
    };
  }, []);

  const login = () => {
    window.location.href = '/auth/login';
  };

  const logout = async () => {
    try {
      await fetch('/auth/logout', { method: 'POST', credentials: 'include' });
    } catch {
      // Offline — clear locally anyway so the UI reflects the intent.
    }
    clearCachedUser();
    setState({ user: null, loading: false });
  };

  return (
    <AuthContext.Provider value={{ ...state, login, logout }}>{children}</AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
